import { Editor } from '@tiptap/react';
import { Box, Typography } from '@mui/material';
import { t } from 'i18next';
import { useCalcEditorState } from 'screens/editorial/asset/hooks/useCalcEditorState';
import { bloxValues } from 'themes/constants';
import { EditorSetupConfig } from 'screens/editorial/asset/utility/getEditorConfigs';

type CountType = 'words' | 'characters';

export type CounterTextProps = {
  type?: CountType;
  count?: number;
  maxLength?: number;
};

export function CounterText({ type, count, maxLength }: CounterTextProps) {
  const label = t(`blox.general.${type}`);
  const max = maxLength ? ` / ${maxLength}` : '';
  const formatted = `${count}${max} ${label}`;
  return (
    <Typography
      id="wordcounter-text"
      data-testid="wordcounter-text"
      sx={(theme) => ({
        fontSize: bloxValues.tinyFontSizeRem,
        color: theme.palette.text.secondary,
      })}
    >
      {formatted}
    </Typography>
  );
}

type WordCountProps = {
  editor: Editor;
  display?: EditorSetupConfig['showCount'];
  maxLength?: number;
};

export default function WordCount({ editor, display, maxLength }: WordCountProps) {
  const editorState = useCalcEditorState({ editor: editor });

  const counts = {
    words: editorState?.wordCount,
    characters: editorState?.characterCount,
  };

  const showWords = display === 'word' || display === 'both';
  const showCharacters = display === true || display === 'both';

  if (!editor) return null;

  return (
    <Box
      id="wordcount-container"
      data-testid="wordcount-container"
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 1,
        my: 1,
        ml: 1,
      }}
    >
      {showWords && (
        <CounterText
          type="words"
          count={counts?.words}
          maxLength={maxLength}
        />
      )}
      {showCharacters && (
        <CounterText
          type="characters"
          count={counts?.characters}
          maxLength={maxLength}
        />
      )}
    </Box>
  );
}
